import { createContext, useContext, useReducer } from 'react';
import {
    DevelopmentContextState,
    DevelopmentDispatch,
    DevelopmentDispatchContextType,
    DevelopmentDispatchHook,
    DevelopmentDispatchTypes,
    DevelopmentProviderProps,
    DevelopmentReducerFunction,
    DevelopmentState,
    DevelopmentStateContextType,
    DevelopmentStateHook,
} from './Development.types';
import { isDevelopment } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { GoMotoComponent } from '../../API/GoMoto.types';

// TODO: Finish adding the ability for endpoint URLs to be changed. This will require reworking all of our current endpoints to be able to use hooks and the Axios context.

// #region Setup
/**
 * Defines the initial state object of the admin context with all properties set to undefined.
 */
const initialState: DevelopmentContextState = {
    isDev: isDevelopment(),
    isTimeoutDisabled: isDevelopment(),
    kioskKey: isDevelopment() ? '' : undefined,
    hideKeyboard: isDevelopment() ? false : true,
    /*
     * appApiRoot: isDevelopment() ? process.env.REACT_APP_GOMOTO_API_ROOT as typeof AppApiRootOptions[keyof typeof AppApiRootOptions] : undefined,
     * appApiUsername: isDevelopment() ? process.env.REACT_APP_GOMOTO_API_USERNAME as typeof AppApiUsernames[keyof typeof AppApiUsernames] : undefined,
     * appApiKey: isDevelopment() ? process.env.REACT_APP_GOMOTO_API_USERNAME as typeof AppApiKeys[keyof typeof AppApiKeys] : undefined,
     */
};

/**
 * Sets up the admin state context and defaults it to undefined so that if any admin context hook is used outside of the
 * admin state context, we can throw an error.
 */
const DevelopmentStateContext: DevelopmentStateContextType = createContext<DevelopmentState>(undefined);

/**
 * Sets up the admin dispatch context and defaults it to undefined so that if any admin hook that uses the dispatch context is
 * used outside of the admin dispatch context, we can throw an error.
 */
const DevelopmentDispatchContext: DevelopmentDispatchContextType = createContext<DevelopmentDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the admin context to update the internal state
 * of the context.
 *
 * @param state - {@link AdminReducerFunction state} description.
 * @param action - {@link AdminReducerFunction action} description.
 */
export const developmentReducer: DevelopmentReducerFunction = (state, action) => {
    if (isDevelopment()) {
        switch (action.type) {
            case DevelopmentDispatchTypes.SetIsDev:
                return {
                    ...state,
                    isDev: action.payload,
                };
            case DevelopmentDispatchTypes.SetTimeoutDisabled:
                return {
                    ...state,
                    isTimeoutDisabled: action.payload,
                };
            case DevelopmentDispatchTypes.SetKiosk:
                return {
                    ...state,
                    kioskKey: action.payload,
                };
            case DevelopmentDispatchTypes.SetHideKeyboard:
                return {
                    ...state,
                    hideKeyboard: action.payload,
                };
            /*
             * case DevelopmentDispatchTypes.SetAppApiRoot:
             *     return {
             *         ...state,
             *         appApiRoot: AppApiRootOptions[action.payload],
             *         appApiUsername: AppApiUsernames[action.payload as keyof typeof AppApiUsernames],
             *         appApiKey: AppApiKeys[action.payload as keyof typeof AppApiKeys]
             *     };
             */
            default:
                return state;
        }
    }
    else {
        return {
            isDev: false,
            isTimeoutDisabled: false,
            kioskKey: undefined,
            hideKeyboard: false,
        };
    }
};
// #endregion

// #region Provider
/**
 * Provides the admin state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link LocalizationProviderComponent children} description.
 */
export const DevelopmentProvider: GoMotoComponent<DevelopmentProviderProps> = ({
    children,
}) => {
    const [state, dispatch] = useReducer<DevelopmentReducerFunction>(developmentReducer, initialState);

    return (
        <DevelopmentStateContext.Provider value={state}>
            <DevelopmentDispatchContext.Provider value={dispatch}>
                {children}
            </DevelopmentDispatchContext.Provider>
        </DevelopmentStateContext.Provider>
    );
};
// #endregion

// #region useDevelopmentState
/**
 * Admin context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the Admin context.
 */
export const useDevelopmentState: DevelopmentStateHook = () => {
    const context = useContext(DevelopmentStateContext);

    if (context === undefined) {
        throw new Error('useDevelopmentState must be used within a DevelopmentProvider');
    }

    return context;
};
// #endregion

// #region useDevelopmentDispatch
/**
 * Admin context hook that returns a dispatch function which can dispatch actions to
 * update the state of the Admin context, or throws an error if not used in a
 * component that is a child of the Admin context.
 */
export const useDevelopmentDispatch: DevelopmentDispatchHook = () => {
    const context = useContext(DevelopmentDispatchContext);

    if (context === undefined) {
        throw new Error('useDevelopmentDispatch must be used within a DevelopmentProvider');
    }

    return context;
};
// #endregion